import React, { useEffect, useState, useCallback, useRef } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Pagination from '../../components/common/Pagination';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import ToastStack, { useToasts } from '../../components/common/Toast';
import { useAuth } from '../../context/AuthContext';
import { getTableCallList, updateTableCallStatus, tableCallStreamUrl } from '../../api/tableCall';
import { getStoreList } from '../../api/store';
import { formatDateTime } from '../../utils/format';

const CALL_TYPE_LABEL = {
  STAFF: '직원호출', PAYMENT: '계산요청', WATER: '물', SPOON: '수저', NAPKIN: '냅킨', SIDE_DISH: '반찬리필', ETC: '기타',
};

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'REQUESTED', label: '접수' },
  { value: 'DONE', label: '처리완료' },
];

const CALL_TYPE_OPTIONS = [
  { value: '', label: '전체 유형' },
  { value: 'STAFF', label: '직원호출' },
  { value: 'PAYMENT', label: '계산요청' },
  { value: 'WATER', label: '물' },
  { value: 'SPOON', label: '수저' },
  { value: 'NAPKIN', label: '냅킨' },
  { value: 'SIDE_DISH', label: '반찬리필' },
  { value: 'ETC', label: '기타' },
];

export default function TableCallManagePage() {
  const { auth } = useAuth();
  const [stores, setStores] = useState([]);
  const [condition, setCondition] = useState({ pageNo: 1, pageSize: 15, storeId: '', callStatus: '', callType: '' });
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resolveTarget, setResolveTarget] = useState(null);
  const { toasts, pushToast, removeToast } = useToasts();

  useEffect(() => {
    getStoreList().then((res) => setStores(res.data || [])).catch(() => setStores([]));
  }, []);

  const fetchList = useCallback(async (cond, silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await getTableCallList(cond);
      setPage(res.data);
    } catch (err) {
      if (!silent) setError(err.response?.data?.message || '직원호출 목록을 불러오지 못했습니다.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(condition); }, [condition, fetchList]);

  // 매장 카운터에 계속 띄워두는 화면이라, 새로 들어온 호출을 놓치지 않도록 가볍게 폴링한다
  // (10초 간격, 조용히 - 로딩 표시 없이. 테이블오더관리와 동일한 방식). 아래 SSE 구독이
  // 대부분의 경우 즉시 갱신해주지만, 연결이 끊긴 순간을 대비한 안전망으로 그대로 둔다.
  useEffect(() => {
    const interval = setInterval(() => fetchList(condition, true), 10000);
    return () => clearInterval(interval);
  }, [condition, fetchList]);

  // 새 호출이 등록/처리되는 즉시(폴링 주기를 기다리지 않고) 목록을 갱신하기 위한 실시간(SSE) 구독.
  // condition/fetchList 는 필터가 바뀔 때마다 새로 만들어지므로, 매번 재연결하지 않도록
  // ref 에 최신 값을 담아두고 이펙트 자체는 accessToken 이 바뀔 때만 재실행한다.
  const fetchListRef = useRef(fetchList);
  const conditionRef = useRef(condition);
  useEffect(() => {
    fetchListRef.current = fetchList;
    conditionRef.current = condition;
  }, [fetchList, condition]);

  useEffect(() => {
    if (!auth?.accessToken) return undefined;
    const eventSource = new EventSource(tableCallStreamUrl(auth.accessToken));
    eventSource.addEventListener('call-changed', (e) => {
      // callStatus 가 "REQUESTED" 인 경우만 "새 호출" 이다 - 관리자가 직접 처리완료
      // 처리했을 때도 같은 이벤트가 오므로, 그 경우까지 토스트를 띄우면 자기 액션 직후에
      // 알림이 뜨는 것처럼 보여 혼란스럽다.
      try {
        const data = JSON.parse(e.data);
        if (data.callStatus === 'REQUESTED') {
          pushToast('새 직원호출이 접수되었습니다.');
        }
      } catch {
        // 파싱에 실패해도 목록 갱신 자체는 계속 진행한다.
      }
      fetchListRef.current(conditionRef.current, true);
    });
    // 연결 오류 시 브라우저가 자동으로 재연결을 시도한다 - 그 사이에도 위 10초 폴링이
    // 안전망으로 계속 동작하므로 별도 처리가 필요 없다.
    eventSource.onerror = () => {};
    return () => eventSource.close();
  }, [auth?.accessToken, pushToast]);

  const handleConfirmResolve = async () => {
    const { call } = resolveTarget;
    try {
      await updateTableCallStatus(call.callId, 'DONE', auth.adminName);
      setResolveTarget(null);
      fetchList(condition);
    } catch (err) {
      pushToast(err.response?.data?.message || '처리완료 처리에 실패했습니다.', 'error');
    }
  };

  return (
    <AdminLayout title="직원호출관리" menuUrl="/admin/table-call">
      <ToastStack toasts={toasts} onDismiss={removeToast} />

      <div className="page-header">
        <div>
          <h1>직원호출관리</h1>
          <div className="subtitle">QR오더 화면에서 고객이 요청한 직원호출/계산요청을 확인하고 처리완료 처리합니다.</div>
        </div>
      </div>

      <div className="list-toolbar">
        <div className="search-bar">
          <select className="select" value={condition.storeId} onChange={(e) => setCondition((c) => ({ ...c, storeId: e.target.value, pageNo: 1 }))}>
            <option value="">전체 매장</option>
            {stores.map((s) => <option key={s.storeId} value={s.storeId}>{s.storeName}</option>)}
          </select>
          <select className="select" value={condition.callStatus} onChange={(e) => setCondition((c) => ({ ...c, callStatus: e.target.value, pageNo: 1 }))}>
            {STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <select className="select" value={condition.callType} onChange={(e) => setCondition((c) => ({ ...c, callType: e.target.value, pageNo: 1 }))}>
            {CALL_TYPE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <button className="btn btn-secondary" onClick={() => fetchList(condition)}>새로고침</button>
        </div>
      </div>

      <div className="list-count-bar">
        <span className="list-count">전체 <b>{page?.totalCount ?? 0}</b>건</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>호출번호</th><th>매장</th><th>테이블</th><th>유형</th><th>요청사항</th><th>상태</th><th>접수일시</th><th>관리</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={8} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && page?.list?.length === 0 && <tr><td colSpan={8} className="empty-row">접수된 호출이 없습니다.</td></tr>}
              {!loading && !error && page?.list?.map((c) => (
                <tr key={c.callId}>
                  <td>{c.callId}</td>
                  <td>{c.storeName}</td>
                  <td>{c.tableNo}</td>
                  <td>{CALL_TYPE_LABEL[c.callType] || c.callType}</td>
                  <td>
                    {c.memo
                      ? <span className="to-memo-callout" title={c.memo}>{c.memo}</span>
                      : <span className="text-tertiary">-</span>}
                  </td>
                  <td><StatusBadge value={c.callStatus} label={STATUS_OPTIONS.find((s) => s.value === c.callStatus)?.label} /></td>
                  <td>{formatDateTime(c.createdAt)}</td>
                  <td className="actions">
                    {c.callStatus === 'REQUESTED' ? (
                      <button type="button" className="btn btn-secondary" onClick={() => setResolveTarget({ call: c })}>
                        처리완료
                      </button>
                    ) : (
                      <span className="text-tertiary">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {page && <Pagination {...page} onChange={(pageNo) => setCondition((c) => ({ ...c, pageNo }))} />}

      {resolveTarget && (
        <ConfirmDialog
          title="직원호출 처리완료"
          message={`호출번호 ${resolveTarget.call.callId} (테이블 ${resolveTarget.call.tableNo}) 를 처리완료 처리하시겠습니까?`}
          confirmLabel="처리완료"
          onCancel={() => setResolveTarget(null)}
          onConfirm={handleConfirmResolve}
        />
      )}
    </AdminLayout>
  );
}
